import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Bot,
  Coins,
  BarChart3,
  MessageCircle,
  Briefcase,
  Code2,
} from "lucide-react";

const FeaturesSection = () => {
  const features = [
    {
      icon: <Bot size={36} />,
      title: "AI-Powered Issue Posting",
      desc: "Our AI helps you write perfect issue titles, tags, and summaries automatically.",
    },
    {
      icon: <Coins size={36} />,
      title: "Earn by Solving",
      desc: "Developers earn money or coins for solving coding issues across technologies.",
    },
    {
      icon: <BarChart3 size={36} />,
      title: "Leaderboard & Reputation",
      desc: "Climb global rankings, earn badges, and showcase your coding credibility.",
    },
    {
      icon: <MessageCircle size={36} />,
      title: "Chat & Collaboration",
      desc: "Chat with issue posters and collaborate live for quicker, better solutions.",
    },
    {
      icon: <Briefcase size={36} />,
      title: "Referral Jobs & Freelance Work",
      desc: "Companies discover top solvers and offer real job & freelance projects.",
    },
    {
      icon: <Code2 size={36} />,
      title: "Portfolio Growth",
      desc: "Each solved issue adds to your developer portfolio automatically.",
    },
  ];

  return (
    <SectionWrapper id="features">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Why Developers <Highlight>Love SolveMyCodeIssue</Highlight>
      </motion.h2>

      <FeaturesContainer>
        {features.map((f, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.15 }}
          >
            <FeatureCard>
              <IconWrapper>{f.icon}</IconWrapper>
              <FeatureTitle>{f.title}</FeatureTitle>
              <FeatureDesc>{f.desc}</FeatureDesc>
            </FeatureCard>
          </motion.div>
        ))}
      </FeaturesContainer>
    </SectionWrapper>
  );
};

export default FeaturesSection;

// ---------------- Styled Components ----------------

const SectionWrapper = styled.section`
  width: 100%;
  min-height: 90vh;
  background: linear-gradient(180deg, #0b0617, #000);
  padding: 100px 20px;
  text-align: center;
  color: #fff;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeaturesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 30px;
  margin-top: 70px;
  padding: 0 40px;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.25);
  border-radius: 16px;
  padding: 40px 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  backdrop-filter: blur(6px);
  transition: all 0.3s ease;
  min-height: 250px; /* ✅ fixed uniform height */
  max-height: 250px;
  justify-content: flex-start;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);
    border-color: #c59fff;
    box-shadow: 0 8px 20px rgba(155, 92, 255, 0.25);
  }
  @media (max-width: 768px) {
    min-height: 300px;
    max-height: 300px;
  }
`;

const IconWrapper = styled.div`
  background: rgba(155, 92, 255, 0.15);
  border-radius: 50%;
  padding: 14px;
  margin-bottom: 20px;
  color: #c59fff;
`;

const FeatureTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #c59fff;

  /* ✅ Single-line truncation with ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const FeatureDesc = styled.p`
  font-size: 0.95rem;
  color: #e0e0e0;
  line-height: 1.6;
  max-width: 280px;
  margin: 0 auto;

  /* ✅ Multi-line ellipsis (2 lines visible max) */
  display: -webkit-box;
  -webkit-line-clamp: 3; /* show 3 lines max */
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
